import cx from 'classnames';
import type { FC } from 'react';

import { Headline, Icon, Tooltip } from '@zen/DesignSystem';
import Divider from '@zen/DesignSystem/ContextMenu/Divider';
import { type SuggestionSource, SuggestionSourceType } from '@zen/graphql/types.generated';
import { formatDateTime } from '@zen/utils/dateTime';

import { RejectPopover } from '../RejectPopover/RejectPopover';

interface SuggestionPillProps {
  id: string;
  onAccept: (id: string) => void;
  onReject: (id: string, reason: string) => void;
  proposedValueText: string;
  source: SuggestionSource;
}

export const SuggestionPill: FC<SuggestionPillProps> = ({ id, proposedValueText, onAccept, onReject, source }) => {
  const { emailMetadata, source: sourceType } = source;

  const renderTooltipContent = () => {
    if (sourceType !== SuggestionSourceType.EMAIL) return proposedValueText;

    const rows = [
      { label: 'Inbox', value: emailMetadata?.monitoredEmail },
      { label: 'Sender', value: emailMetadata?.sender },
      { label: 'Subject', value: emailMetadata?.subject },
      {
        label: 'Received',
        value: emailMetadata?.timestamp && formatDateTime(emailMetadata.timestamp, 'D_MMMM_YYYY_HH_MM')
      }
    ].filter((row) => row.value);

    return (
      <div className="bg-sunset-light p-0.5 rounded-md max-w-sm text-navy-dark">
        <div className="bg-white rounded-sm flex flex-col p-4">
          <Headline className="font-bold text-transparent bg-clip-text bg-sunset-base mb-2" level={3}>
            Document received by email - approval required
          </Headline>
          <p className="text-sm text-grey-base">
            Luca has suggested this document from an email. Please verify if it belongs to this shipment.
          </p>
          <Divider />
          <div className="text-sm font-bold">Source</div>
          <div className="flex flex-col gap-2 mt-2">
            {rows.map(({ label, value }) => (
              <div key={label} className="flex items-center justify-between text-sm">
                <p className="text-grey-base">{label}</p>
                <p className="text-black">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const containerClassName = cx(
    'bg-sunset-light rounded-4xl flex items-center px-2 gap-2 justify-between w-auto h-8 text-navy-dark',
    {
      'mt-2': sourceType !== SuggestionSourceType.EMAIL
    }
  );

  return (
    <div className={containerClassName}>
      <Tooltip
        className="border-0 outline-0 bg-white rounded-md m-0 p-0"
        compactView={true}
        tooltipContent={renderTooltipContent()}
      >
        <div className="flex items-center gap-2">
          <Icon icon="zicon-sparkles-empty" />
          <span className="font-extrabold truncate max-w-40">{proposedValueText}</span> suggested
        </div>
      </Tooltip>

      <div className="flex gap-1 items-center w-1/3 justify-end">
        <Icon onClick={() => onAccept(id)} className="text-navy-dark right-4 cursor-pointer" icon="zicon-tick" />
        <RejectPopover id={id} onReject={onReject} />
      </div>
    </div>
  );
};
